import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { FaSave, FaArrowLeft, FaPlus, FaTrash } from "react-icons/fa";
import { IoSchool } from "react-icons/io5";
import { toast } from "react-toastify";
import { addGrades, clearState, fetchSettingsGrades } from "../../redux/Slices/GradesSlice";
import { validateFieldLanguage } from "../../Utils/languageValidation";

const AddGrade = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name_ar: "",
    name_en: "",
    status: "",
  });

  // هل للصف شُعب مخصّصة (زي علمي/أدبي للثانوي)؟
  // لو مقفول: الباك اند بيعمل شعبة واحدة تلقائيًا باسم الصف (مناسب لتالتة إعدادي).
  const [hasSections, setHasSections] = useState(false);
  const [sections, setSections] = useState([{ name_ar: "", name_en: "" }]);

  const { settingsGrades, isLoading: loading, error, success } = useSelector((state) => state.grades);

  useEffect(() => {
    dispatch(clearState());
    if (!settingsGrades || settingsGrades.length === 0) {
      dispatch(fetchSettingsGrades());
    }
  }, [dispatch, settingsGrades]);

  useEffect(() => {
    if (success) {
      toast.success("تمت الإضافة بنجاح");
      dispatch(clearState());
      navigate("/grades");
    }
    if (error) {
      toast.error("لم تتم الإضافة");
      dispatch(clearState());
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ── إدارة صفوف الشُّعب ──────────────────────────────
  const handleSectionChange = (index, field, value) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const addSectionRow = () => {
    setSections((prev) => [...prev, { name_ar: "", name_en: "" }]);
  };

  const removeSectionRow = (index) => {
    setSections((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // BUG-11 fix: التحقق من لغة الحقول قبل الإرسال
    const arError = validateFieldLanguage(formData.name_ar, "ar");
    if (arError) return toast.error(arError);
    const enError = validateFieldLanguage(formData.name_en, "en");
    if (enError) return toast.error(enError);

    // التحقق من عدم تكرار اسم الصف الدراسي (عربي أو إنجليزي)
    const isDuplicateAr = Array.isArray(settingsGrades) && settingsGrades.some(
      (g) => g.name_ar.trim().toLowerCase() === formData.name_ar.trim().toLowerCase()
    );
    if (isDuplicateAr) {
      return toast.error("اسم الصف الدراسي بالعربي مكرر بالفعل");
    }

    const isDuplicateEn = Array.isArray(settingsGrades) && settingsGrades.some(
      (g) => g.name_en.trim().toLowerCase() === formData.name_en.trim().toLowerCase()
    );
    if (isDuplicateEn) {
      return toast.error("اسم الصف الدراسي بالإنجليزي مكرر بالفعل");
    }

    const payload = { ...formData };

    // لو فعّل الشُّعب: تحقّق منها وأرسلها؛ غير كده الباك اند بيعمل شعبة تلقائية.
    if (hasSections) {
      const cleaned = sections.map((s) => ({
        name_ar: s.name_ar.trim(),
        name_en: s.name_en.trim(),
      }));

      if (cleaned.some((s) => !s.name_ar || !s.name_en)) {
        return toast.error("من فضلك أكمل اسم كل شعبة بالعربي والإنجليزي");
      }

      for (const s of cleaned) {
        const secAr = validateFieldLanguage(s.name_ar, "ar");
        if (secAr) return toast.error(secAr);
        const secEn = validateFieldLanguage(s.name_en, "en");
        if (secEn) return toast.error(secEn);
      }

      const arNames = cleaned.map((s) => s.name_ar.toLowerCase());
      const enNames = cleaned.map((s) => s.name_en.toLowerCase());
      if (new Set(arNames).size !== arNames.length || new Set(enNames).size !== enNames.length) {
        return toast.error("فيه أسماء شُعب مكررة");
      }

      payload.sections = cleaned;
    }

    dispatch(clearState());
    dispatch(addGrades(payload));
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 9,
    border: "1.5px solid #e2e8f0",
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    background: "#fff",
    transition: "border-color 0.2s, box-shadow 0.2s"
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 700,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
    textAlign: "right"
  };

  const focusHandler = (e) => {
    e.target.style.borderColor = "var(--main-color)";
    e.target.style.boxShadow = "0 0 0 3px rgba(228, 109, 23, 0.1)";
  };

  const blurHandler = (e) => {
    e.target.style.borderColor = "#e2e8f0";
    e.target.style.boxShadow = "none";
  };

  return (
    <div className="container-fluid" style={{ direction: "rtl", padding: "4px 0" }}>
      <style>{`
        .grade-custom-select {
          width: 100% !important;
          padding: 10px 14px !important;
          padding-left: 40px !important;
          border-radius: 9px !important;
          border: 1.5px solid #e2e8f0 !important;
          font-size: 14px !important;
          outline: none !important;
          box-sizing: border-box !important;
          background-color: #fff !important;
          appearance: none !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>") !important;
          background-repeat: no-repeat !important;
          background-position: left 14px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.2s, box-shadow 0.2s !important;
        }
        .grade-custom-select:focus {
          border-color: var(--main-color) !important;
          box-shadow: 0 0 0 3px rgba(228, 109, 23, 0.1) !important;
        }
        .sec-switch { position: relative; display: inline-block; width: 46px; height: 26px; flex: 0 0 auto; }
        .sec-switch input { opacity: 0; width: 0; height: 0; }
        .sec-slider {
          position: absolute; cursor: pointer; inset: 0; background: #cbd5e1;
          transition: .2s; border-radius: 26px;
        }
        .sec-slider:before {
          position: absolute; content: ""; height: 20px; width: 20px; right: 3px; bottom: 3px;
          background: #fff; transition: .2s; border-radius: 50%;
        }
        .sec-switch input:checked + .sec-slider { background: var(--main-color); }
        .sec-switch input:checked + .sec-slider:before { transform: translateX(-20px); }
      `}</style>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <div>
          <h4 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: 20, display: "flex", alignItems: "center", gap: 8 }}>
            <IoSchool style={{ color: "var(--gray-color)" }} /> إضافة صف جديد
          </h4>
          <p style={{ margin: "2px 0 0", color: "#64748b", fontSize: 13 }}>تحديد اسم وتفاصيل الصف الدراسي الجديد باللغتين العربية والإنجليزية</p>
        </div>
        <Link to="/grades" style={{ textDecoration: "none" }}>
          <button style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 18px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            <FaArrowLeft size={11} /> العودة
          </button>
        </Link>
      </div>

      <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", padding: "24px", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>الاسم (عربي) *</label>
              <input
                type="text"
                name="name_ar"
                value={formData.name_ar}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="مثال: الصف الأول الثانوي"
                required
              />
            </div>

            <div>
              <label style={labelStyle}>الاسم (إنجليزي) *</label>
              <input
                type="text"
                name="name_en"
                value={formData.name_en}
                onChange={handleInputChange}
                style={inputStyle}
                onFocus={focusHandler}
                onBlur={blurHandler}
                placeholder="مثال: First Grade Secondary"
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 24, maxWidth: "50%" }}>
            <label style={labelStyle}>الحالة *</label>
            <select
              className="grade-custom-select"
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
            >
              <option value="" disabled>اختر الحالة</option>
              <option value="1">🟢 مفعل</option>
              <option value="0">🔴 غير مفعل</option>
            </select>
          </div>

          {/* ── توجل الشُّعب ─────────────────────────────── */}
          <div style={{ marginBottom: 20, padding: "16px 18px", borderRadius: 12, border: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>لهذا الصف شُعب؟</div>
                <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>
                  {hasSections
                    ? "أضف الشُّعب (مثل: علمي، أدبي)."
                    : "بدون تفعيل، سيتم إنشاء شعبة واحدة تلقائيًا باسم الصف."}
                </div>
              </div>
              <label className="sec-switch">
                <input
                  type="checkbox"
                  checked={hasSections}
                  onChange={(e) => setHasSections(e.target.checked)}
                />
                <span className="sec-slider"></span>
              </label>
            </div>

            {hasSections && (
              <div style={{ marginTop: 16 }}>
                {sections.map((sec, index) => (
                  <div
                    key={index}
                    style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end", marginBottom: 12 }}
                  >
                    <div>
                      <label style={labelStyle}>اسم الشعبة (عربي) *</label>
                      <input
                        type="text"
                        value={sec.name_ar}
                        onChange={(e) => handleSectionChange(index, "name_ar", e.target.value)}
                        style={inputStyle}
                        onFocus={focusHandler}
                        onBlur={blurHandler}
                        placeholder="مثال: علمي"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>اسم الشعبة (إنجليزي) *</label>
                      <input
                        type="text"
                        value={sec.name_en}
                        onChange={(e) => handleSectionChange(index, "name_en", e.target.value)}
                        style={inputStyle}
                        onFocus={focusHandler}
                        onBlur={blurHandler}
                        placeholder="e.g. Scientific"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSectionRow(index)}
                      disabled={sections.length === 1}
                      title="حذف الشعبة"
                      style={{
                        height: 42,
                        width: 42,
                        borderRadius: 9,
                        border: "1px solid #fecaca",
                        background: sections.length === 1 ? "#f1f5f9" : "#fef2f2",
                        color: sections.length === 1 ? "#cbd5e1" : "#ef4444",
                        cursor: sections.length === 1 ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FaTrash size={13} />
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addSectionRow}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 16px",
                    borderRadius: 9,
                    border: "1px dashed var(--main-color)",
                    background: "#fff",
                    color: "var(--main-color)",
                    fontWeight: 700,
                    cursor: "pointer",
                    fontSize: 13,
                  }}
                >
                  <FaPlus size={11} /> إضافة شعبة
                </button>
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate("/grades")}
              style={{ padding: "10px 20px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontWeight: 600, cursor: "pointer", fontSize: 14 }}
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "10px 28px",
                borderRadius: 10,
                border: "none",
                background: loading ? "#c7d2fe" : "linear-gradient(135deg,var(--main-color),var(--main-light))",
                color: "#fff",
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 14
              }}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm" />
                  جاري الحفظ...
                </>
              ) : (
                <>
                  <FaSave size={12} />
                  حفظ الصف
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGrade;
